import { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { FormAutocomplete, FormDialog, FormTextField, useConfirmation } from '../../../design-system';
import { canReparentOrganization, type OrganizationPlanNode } from '../organizationPlan';
import { studioOrganizationPath } from '../organizationStudio';

export function OrganizationStudioRenameDialog({ node, disabled, onClose, onApply, onDirtyChange }: {
  node: OrganizationPlanNode; disabled: boolean;
  onClose: () => void; onApply: (name: string) => void; onDirtyChange: (dirty: boolean) => void;
}) {
  const [name, setName] = useState(node.name);
  const { confirm, confirmationDialog } = useConfirmation();
  const changed = name.trim() !== node.name;
  const requestClose = async () => {
    if (changed && !await confirm({ title: '입력한 변경을 버릴까요?', description: '조직명 변경은 아직 발령안에 적용되지 않았습니다.', confirmLabel: '변경 버리기', cancelLabel: '계속 입력', intent: 'discard' })) return;
    onClose();
  };
  useEffect(() => { onDirtyChange(changed); return () => onDirtyChange(false); }, [changed, onDirtyChange]);
  return <><FormDialog open title="조직명 변경" maxWidth="xs" mobilePresentation="dialog" onClose={() => void requestClose()} cancelLabel="취소" submitLabel="변경 적용"
    submitDisabled={disabled || !changed || !name.trim()} onSubmit={() => { if (!disabled && changed && name.trim()) onApply(name.trim()); }}>
    <FormTextField required autoFocus label="조직명" value={name} disabled={disabled} onChange={event => setName(event.target.value)} slotProps={{ htmlInput: { maxLength: 200 } }} />
  </FormDialog>{confirmationDialog}</>;
}

/** 드래그를 사용할 수 없는 키보드·터치 환경의 동일한 조직 이동 동작입니다. */
export function OrganizationStudioMoveDialog({ node, nodes, disabled, onClose, onMove }: {
  node: OrganizationPlanNode; nodes: readonly OrganizationPlanNode[]; disabled: boolean;
  onClose: () => void; onMove: (parentId: string) => void;
}) {
  const [parentId, setParentId] = useState<string>();
  const parentField = useRef<HTMLDivElement>(null);
  const options = nodes.filter(parent => canReparentOrganization(nodes, node.id, parent.id));
  const parent = options.find(item => item.id === parentId) ?? null;
  return <FormDialog open title="조직 이동" maxWidth="xs" mobilePresentation="dialog" onClose={onClose} cancelLabel="취소" submitLabel="이동"
    onEntered={() => parentField.current?.querySelector('input')?.focus()}
    submitDisabled={disabled || !parent} onSubmit={() => { if (!disabled && parent) onMove(parent.id); }}>
    <Typography variant="subtitle2">{node.name}</Typography>
    <Box ref={parentField}><FormAutocomplete options={options} value={parent} disabled={disabled}
      getOptionLabel={option => option.name} getOptionKey={option => option.id} isOptionEqualToValue={(option, value) => option.id === value.id}
      onChange={(_, value) => setParentId(value?.id)}
      renderOption={({ key, ...props }, option) => <li key={key} {...props}><Stack sx={{ minWidth: 0 }}><Typography variant="body2">{option.name}</Typography><Typography variant="caption" color="text.secondary">{studioOrganizationPath(nodes, option.id)}</Typography></Stack></li>}
      input={{ label: '이동할 상위 조직', required: true, placeholder: '조직 검색' }} /></Box>
  </FormDialog>;
}
